import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { AuthRedirectService } from '../services/redirect.service';
import { InviteApiService } from '../services/api/invite-api.service';
import { ProjectService } from '../services/project.service';
import { InviteTransitionService } from '../../shared';
import { waitForAuthInit } from './wait-for-auth-init';

/**
 * Guard que impede usuários autenticados de acessarem páginas de login/registro.
 * Redireciona para a página apropriada baseada no status do usuário.
 */
export const guestGuard: CanActivateFn = async (route) => {
  const authService = inject(AuthService);
  const authRedirectService = inject(AuthRedirectService);
  const inviteApiService = inject(InviteApiService);
  const inviteTransitionService = inject(InviteTransitionService);
  const projectService = inject(ProjectService);

  // Aguarda a inicialização do auth de forma reativa (sem janela de corrida)
  await waitForAuthInit(authService);

  // Se o usuário está autenticado, não pode acessar login/register
  if (authService.isAuthenticated()) {
    const inviteToken = route.queryParamMap.get('token')?.trim();
    const currentUserEmail = authService.currentUser()?.email?.trim().toLowerCase();

    if (inviteToken && currentUserEmail) {
      await acceptInviteWithConfirmation(inviteToken, currentUserEmail);
    }

    // Usar o serviço de redirecionamento existente
    authRedirectService.redirectAfterLogin();
    return false;
  }

  return true;

  /**
   * Como a conta já existe, o vínculo nunca é trocado sem que o usuário veja a
   * transição de organização e confirme explicitamente.
   */
  async function acceptInviteWithConfirmation(token: string, email: string): Promise<void> {
    try {
      const invite = await inviteApiService.validateInviteToken(token);

      if (!invite || invite.isNewUser) {
        return;
      }

      const confirmed = await inviteTransitionService.confirmTransition(invite);

      if (confirmed) {
        await inviteApiService.acceptInvitePublic(token, email);

        // O aceite troca a organização no back-end: descarta os projetos da
        // empresa anterior e recarrega a sessão para refletir o novo vínculo
        // antes do redirecionamento para /projects.
        projectService.clearAllProjects();
        await authService.loadSession();
      }
    } catch {
      // Token já consumido, inválido ou falha no aceite: mantém o fluxo normal
      // de redirecionamento, sem alterar o vínculo atual do usuário.
    }
  }
};
